import api from 'src/utils/ajax';
import { decamelize } from 'src/utils/decamelize';

import { FranchiseFee } from './_types';
import { denormalizeFranchiseFee, normalizeFranchiseFee } from './normalizers';

export * from './_types';

export const FranchiseFeesQueryKey = 'franchise-fees';

export const fetchFranchiseFeesQueryKey = (params?: FranchiseFeesParams) => [
  FranchiseFeesQueryKey,
  params,
];

export type FranchiseDataParams = Omit<
  FranchiseFee,
  'id' | 'createdAt' | 'updatedAt'
>;

export interface FranchiseFeesParams {
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  page?: number;
  perPage?: number;
  search?: string;
  filters?: {
    feeType?: string;
    startDateRange?: {
      start: string;
      end: string;
    };
    endDateRange?: {
      start: string;
      end: string;
    };
    active?: number;
  };
}

export const fetchFranchiseFees = async ({
  sortBy,
  sortDir,
  page = 0,
  perPage = 20,
  search = '',
  filters = {},
}: FranchiseFeesParams) => {
  const params = {
    page: page,
    per: perPage,
    ...(search && { 'q[name_cont]': search }),
    ...(sortBy && {
      'q[s]': `${decamelize(sortBy || '')} ${sortDir || 'desc'}`,
    }),
    ...(filters.feeType && { 'q[fee_type_eq]': filters.feeType }),
    ...(filters.startDateRange && {
      'q[start_date_gteq]': filters.startDateRange.start,
      'q[start_date_lteq]': filters.startDateRange.end,
    }),
    ...(filters.endDateRange && {
      'q[end_date_gteq]': filters.endDateRange.start,
      'q[end_date_lteq]': filters.endDateRange.end,
    }),
    ...(filters.active == 1 && { 'q[active_eq]': true }),
    ...(filters.active == 0 && { 'q[active_eq]': false }),
  };
  const { data } = await api.get('/api/v4/hq/franchise_fees', { params });

  // Handle both paginated and non-paginated responses
  if (data.franchise_fees && data.meta?.total !== undefined) {
    // Paginated response
    return {
      franchiseFees: data.franchise_fees.map(normalizeFranchiseFee),
      total: data.meta.total,
    };
  } else if (Array.isArray(data)) {
    // Non-paginated response (fallback)
    return {
      franchiseFees: data.map(normalizeFranchiseFee),
      total: data.length,
    };
  } else {
    // Handle other response formats
    return {
      franchiseFees: [],
      total: 0,
    };
  }
};

export const createFranchiseFee = async (
  data: FranchiseDataParams
): Promise<FranchiseFee> => {
  const { data: response } = await api.post(`/api/v4/hq/franchise_fees`, {
    hq_franchise_fee: denormalizeFranchiseFee(data),
  });
  return normalizeFranchiseFee(response);
};

export const updateFranchiseFee = async (
  id: string,
  data: FranchiseDataParams
): Promise<FranchiseFee> => {
  const { data: response } = await api.put(`/api/v4/hq/franchise_fees/${id}`, {
    hq_franchise_fee: denormalizeFranchiseFee(data),
  });
  return normalizeFranchiseFee(response);
};

export const deleteFranchiseFee = async (id: string): Promise<FranchiseFee> => {
  const { data: response } = await api.delete(
    `/api/v4/hq/franchise_fees/${id}`
  );
  return normalizeFranchiseFee(response);
};

export const fetchFranchiseFee = async (id: string): Promise<FranchiseFee> => {
  const { data } = await api.get(`/api/v4/hq/franchise_fees/${id}`);
  return normalizeFranchiseFee(data);
};
